import React, { useState, useEffect } from "react";
import moment from "moment";
import {
    Form,
    Input,
    Button,
    Modal,
    message,
    Radio,
    DatePicker,
    Upload
} from "antd";
import { createUseStyles } from "react-jss";
import ReCAPTCHA from "react-google-recaptcha";
import * as lodash from "lodash";
import { useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";
import { EyeOutlined, EyeInvisibleOutlined, UploadOutlined } from "@ant-design/icons";
import { GOOGLE_RECAPTCHA_SITE_KEY } from "../../../globals";
import {
  putImageS3,
    riderOnboardingPostFunction,
} from "../../../api/appUserSignup";

const styles = createUseStyles({
    header: {
        padding: "8px 24px",
        boxShadow: "0px 0px 10px rgba(0,0,0,0.4)",
        fontSize: 16,
        fontWeight: 600,
        color: "#424242",
        alignItems: "center",
        position: "fixed",
        top: 0,
        right: 0,
        left: 0,
        background: "#ffffff",
        zIndex: +1,
    },
    bookingFormFields: {
        padding: 36,
        boxSizing: "border-box",
        "& label::after": {
            content: '"" !important',
        },
        "& label.ant-form-item-required::before": {
            display: "inline-block",
            marginRight: "4px",
            color: "#ff4d4f",
            fontSize: "14px",
            lineHeight: 1,
            content: '"*" !important',
        },
        "& label.ant-radio-wrapper": {
            minWidth: "120px",
        },
        "& .ant-form-item-control-input": {
            maxWidth: "400px",
        },
        "& .ant-input-number ": {
            width: "100%",
        },
    },
    phoneNumberField: {
        "& .ant-input-group-rtl": {
            direction: "ltr",
            borderLeft: "1px solid #d9d9d9",
        },
    },
    formSectionHeader: {
        fontSize: 16,
        fontWeight: 600,
        color: "#424242",
        marginBottom: 20,
    },
    uploadField: {
        display: "flex",
        "& p": {
            color: "#A6B6C3",
            fontSize: "0.8rem",
        },
        "& .ant-upload-select-picture-card": {
            width: "100%",
            height: "auto",
            marginRight: 0,
            marginBottom: 0,
        },
        "& .ant-upload-list-picture-card": {
            width: "100%",
        },
        "& .ant-upload": {
            flexDirection: "column",
        },
    },
    uploadIcon: {
        fontSize: "16px",
        color: "#A6B6C3",
        padding: "10px 0",
    },
    verifyButton: {
        color: "#1890ff",
        cursor: 'pointer',
        fontWeight: 500,
    },
});

const DealshareRiderOnboardingForm = (props: any) => {
    const intl = useIntl();
    const classes = styles();

    const [googleCaptcha, setGoogleCaptcha] = useState(null);
    const [captchaError, setCaptchaError] = useState("");

    const [aadharCardGoogleCaptcha, setAadharCardGoogleCaptcha] = useState(null);
    const [aadharCardCaptchaError, setAadharCardCaptchaError] = useState("");
    const [aadhaarCardTransactionId, setAadharCardTransactionId] = useState(null);
    
    const [visibleOtp, setVisibleOtp] = useState(false);
    const [aadhaarCardVerificationHash, setAadhaarCardVerificationHash] = useState(null);

    const toggleVisibility = () => {
        setVisibleOtp((prev) => !prev);
    };

    const [processingSubmit, setProcessingSubmit] = useState(false);
    const [processingVerify, setProcessingVerify] = useState(false);
    const [isGeneratingOTP, setGeneratingOTP] = useState(false);
    const [formReset, setFormReset] = useState(0);
    const language = useSelector((state: any) => state.translation.language);

    const [form] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();
    const [aadharCardVerified, setAadharCardVerified] = React.useState(false);
    const [isPresentPermanentAddressSame, setIsPresentPermanentAddressSame] = React.useState(false);
    const [identificationDocumentList, setIdentificationDocumentList] = React.useState<any>({});

    useEffect(() => {
        setGoogleCaptcha(null);
        setAadharCardGoogleCaptcha(null);
    }, [language]);

    const submitForm = async (values: any) => {
        setProcessingSubmit(true);

        if (!googleCaptcha) {
          setCaptchaError(intl.formatMessage({ id: "captchaError" }));
          setProcessingSubmit(false);
          return;
        }

        if (!aadhaarCardTransactionId){
          message.error('Please verify Aadhar Card Number!');
          setProcessingSubmit(false);
          return;
        }

        if (!values?.bank_proof_document?.fileList?.length) {
          message.error('Please upload Bank Proof Document!');
          setProcessingSubmit(false);
          return;
        }

        const fileItem = values?.bank_proof_document?.fileList?.[0];
        const contentType = fileItem.type?.split("/")[1];
        const pocContentTypeList = [{ type: 'bankAccount', contentTypeList: [contentType] }];
        const identificationDocuments = [{ type: 'bankProofDocument', image: '' }];

        const requestData = {
          ...values,
          poc_content_type_list: pocContentTypeList,
          identification_documents: identificationDocuments,
          type: "CREATEWORKER",
          objectType: "WORKER",
          captcha: googleCaptcha,
          date_of_birth: moment(values.date_of_birth).format('YYYY-MM-DD'),
          aadhaar_verification_hash: aadhaarCardVerificationHash,
          aadhaar_verification_status: aadharCardVerified ? 'verified': 'failed',
          driving_licence_number: values?.driving_licence_number?.toUpperCase() || null,
          pan_number: values?.pan_number?.toUpperCase() || null,
        } as { [key: string]: any };

        if(isPresentPermanentAddressSame){
          requestData.present_address = lodash.cloneDeep(values.permanent_address || {});
          delete requestData?.present_address?.care_of;
        }
       
        delete requestData?.is_present_permanent_address_same;
        const response = await riderOnboardingPostFunction('SUBMIT_URL',requestData);
        if (response.isSuccess) {
          await uploadImages(response?.data?.data?.bankAccountNumberSignedUrlList, 'bankProofDocument');
          modal.success({
              title: "Registration Successful",
              content: response?.data?.message ||
                `Your partnering request has been received at Dealshare. We have initiated the background verification for you.
                  You will receive an update via SMS on your registered mobile number once the verification is completed`,
              onOk() {
                  form.resetFields();
                  setProcessingSubmit(false);
              },
          });
        } else {
            modal.error({
                title: "Could Not Process Request",
                content: response.errorMessage,
                onOk() {
                    setProcessingSubmit(false);
                },
            });
        }
    };

    const captchaChange = (captchaVal: any) => {
        setGoogleCaptcha(captchaVal);
        if (captchaVal && captchaError) {
            setCaptchaError("");
        }
    };

    const aadharCardCaptchaChange = (captchaVal: any) => {
        setAadharCardGoogleCaptcha(captchaVal);
        if (captchaVal && aadharCardCaptchaError) {
            setAadharCardCaptchaError("");
        }
    };

    const genderList = [
        {
            key: intl.formatMessage({ id: "genderMale" }),
            value: 'M'
        },
        {
            key: intl.formatMessage({ id: "genderFemale" }),
            value: 'F'
        },
        {
            key: intl.formatMessage({ id: "genderTransgender" }),
            value: 'T'
        }
    ];

    const genderMapEnum = {
      MALE: 'M',
      FEMALE: 'F',
      TRANSGENDER: 'T'
    };

    const generateGenderOptions = () => {
      return genderList.map((entry) => (
        <Radio value={entry?.value}>{entry?.key}</Radio>
      ));
    };

    const handleNameChange = (e, formField) => {
        // remove trailing space or special characters
        if (e.target.value) {
            let value = e.target.value.replace(/[^a-zA-Z\s.'-]/g, '');
            value = (value || '').replace(/\s+$/, ' ')
            form.setFieldValue(formField, value);
        }
        else {
            form.setFieldValue(formField, '');
        }
    }

    const validateAge = (rule, value) => {
        if (value && moment().diff(value, 'years') < 18) {
            return Promise.reject('You must be at least 18 years old!');
        }
        return Promise.resolve();
    }

    const uploadImages = async (signedUrl, type) => {
      try {
        const fileUploads = identificationDocumentList[type].map(async (image, index) => {
          const fileItem = identificationDocumentList[type]?.[index];
          const file = new Blob([fileItem.originFileObj as BlobPart], {
            type: fileItem.type,
          });
  
          await putImageS3(signedUrl, file, fileItem.type);
        });
        await Promise.all(fileUploads);
        return fileUploads;
      } catch (err) {
        modal.error({
          title: "Could Not Process Request",
          content: "Please try again later",
          onOk() {
            setProcessingSubmit(false);
          },
        });
      }
    };
    
    const verifyAadharCardAndCaptcha = async () => {
      setGeneratingOTP(true);
      const aadhaarNumber = form.getFieldValue('aadhaar_number');
      const phoneNumber = form.getFieldValue('phone_number');
      const captcha = aadharCardGoogleCaptcha;

      if (!aadhaarNumber) {
          message.error('Please enter Aadhar Card Number!');
          setGeneratingOTP(false);
          return;
      }

      if (!phoneNumber){
        message.error('Please enter Phone Number!');
        setGeneratingOTP(false);
        return;
      }

      if (!captcha) {
        setAadharCardCaptchaError(intl.formatMessage({ id: "captchaError" }));
        setGeneratingOTP(false);
        return;
      }

      const response = await riderOnboardingPostFunction('AADHAAR_GENERATION_URL', {
        aadhaar_number: aadhaarNumber,
        captcha
      });

      /**
       * success flag is sent explicity in WB as 200 statuses with failures are expected at on-grid end. 
       * Hence standard + data level checks added it if blocks for consistent behavious
      */
      if (response.isSuccess) {
        setAadharCardTransactionId(response?.data?.transaction_id);
        message.success(response?.data?.message || 'OTP Sent to your registered mobile number!');
      } else {
        message.error(response.errorMessage || 'Unable to authenticate Aadhar Details!');
      }
      setGeneratingOTP(false);
    }

    const validateOtp = async () => {
      setProcessingVerify(true);
      const aadhaarNumber = form.getFieldValue('aadhaar_number');
      const phoneNumber = form.getFieldValue('phone_number');
      const otp = form.getFieldValue('otp');
      
      if(!aadhaarNumber){
        message.error('Please enter Aadhar Card Number!');
        setProcessingVerify(false);
        return;
      }

      if(!phoneNumber){
        message.error('Please enter Phone Number!');
        setProcessingVerify(false);
        return;
      }

      if(!otp){
        message.error('Please enter OTP!');
        setProcessingVerify(false);
        return;
      }

      if(!aadhaarCardTransactionId){
        /**
         * Ideally this won't happen as we never revoke the aadharCardTransactionId once set properly.
         * Just in case, we skip the execution part of this block if txn id is missing.
        */
        message.error('Please refresh the page and try again!');
        setProcessingVerify(false);
        return;
      }

      const payload = {
        aadhaar_number: aadhaarNumber,
        otp,
        transaction_id: aadhaarCardTransactionId,
        phone_number: phoneNumber
      };

      const response = await riderOnboardingPostFunction('AADHAAR_VALIDATION_URL', payload);
      /**
       * success flag is sent explicity in WB as 200 statuses with failures are expected at on-grid end. 
       * Hence standard + data level checks added it if blocks for consistent behavious
      */
      if(response.isSuccess){
        const { aadhaar_verification_hash: aadhaarVerificationHash, name: aadhaarCardName,
          date_of_birth: aadhaarCardDateOfBirth, gender: aadhaarCardGender,
        } = response?.data || {};
        if(aadhaarVerificationHash){
          setAadhaarCardVerificationHash(aadhaarVerificationHash);
          form.setFieldValue('name', aadhaarCardName);
          form.setFieldValue('date_of_birth', aadhaarCardDateOfBirth ? moment(aadhaarCardDateOfBirth, 'YYYY-MM-DD') : null);
          form.setFieldValue('gender', lodash.get(genderMapEnum,aadhaarCardGender) || 'M');
          setAadharCardVerified(true);
        }
      } else {
        message.error(response.errorMessage);
      }
      setProcessingVerify(false);
    };

    return (<>
        <Form
            form={form}
            onFinish={submitForm}
            labelCol={{ span: 5 }}
            autoComplete="off"
            wrapperCol={{ span: 12 }}
            labelAlign="left"
        >
            {contextHolder}
            <div className={classes.bookingFormFields}>
              <div className={classes.formSectionHeader}>
                {intl.formatMessage({ id: "basicDetails" })} :
              </div>
              <Form.Item
                label={`${intl.formatMessage({ id: "fullName" })} ::`}
                name="name"
                rules={[
                    {
                        required: true,
                        message: `${intl.formatMessage({ id: "fullName" })} is required!`,
                    }
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({ id: "fullName" })}
                  value={form.getFieldValue('name')}
                  onChange={(e) => handleNameChange(e, 'name')}
                  disabled={aadharCardVerified || processingVerify || processingSubmit}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "dob" })} ::`}
                name="date_of_birth"
                rules={[
                    {
                        required: true,
                        message: `${intl.formatMessage({ id: "dob" })} is required!`,
                    },
                    {
                        validator: validateAge
                    }
                ]}
              >
                <DatePicker
                    style={{ width: '100%' }}
                    disabled={aadharCardVerified || processingVerify || processingSubmit}
                    defaultPickerValue={moment().subtract(18, "years")}
                    disabledDate={(current) => current && current > moment().subtract(18, "years")}
                    placeholder={intl.formatMessage({ id: "dob" })}
                    format={'YYYY-MM-DD'}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "gender" })} ::`}
                name="gender"
                rules={[
                    {
                      required: true,
                      message: `${intl.formatMessage({ id: "gender" })} is required!`,
                    },
                ]}
              >
                <Radio.Group
                  disabled={aadharCardVerified || processingVerify || processingSubmit}
                >
                  {generateGenderOptions()}
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "phoneNumber" })} ::`}
                name="phone_number"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "phoneNumber" })} is required!`,
                  },
                  {
                    pattern: new RegExp(/^[0-9]{10}$/),
                    message: `${intl.formatMessage({ id: "phoneNumber" })} should be 10 digits!`,
                  }
                ]}
              >
                <Input
                  onChange={(e) => {
                      const numberOnly = e.target.value.replace(/\D/g, "");
                      form.setFieldValue('phone_number', numberOnly);
                  }}
                  disabled={aadharCardVerified || processingVerify || processingSubmit}
                  maxLength={10}
                  placeholder={intl.formatMessage({ id: "phoneNumber" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: 'aadhaarCardNumber' })} ::`}
                name={"aadhaar_number"}
                rules={[
                    {
                        required: true,
                        message: `${intl.formatMessage({ id: "aadharCardNumber" })} is required!`,
                    },
                    {
                      pattern: new RegExp(/^[0-9]{12}$/),
                      message: `${intl.formatMessage({ id: "aadharCardNumber" })} should be 12 digits!`,
                    }
                ]}
              >
                <Input
                    placeholder={intl.formatMessage({ id: 'aadhaarCardNumber' })}
                    maxLength={12}
                    onChange={(e) => {
                        const numberOnly = e.target.value.replace(/\D/g, "");
                        form.setFieldValue('aadhaar_number', numberOnly);
                    }}
                    disabled={aadharCardVerified || processingVerify || processingSubmit}
                />
              </Form.Item>
              {!aadharCardVerified ?
                (<Form.Item label=":">
                  <div>
                      <ReCAPTCHA
                          sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                          onChange={aadharCardCaptchaChange}
                          hl={language}
                          key={`${language}${formReset}`}
                      />
                      <span style={{ color: "red" }}>{aadharCardCaptchaError}</span>
                  </div>
                </Form.Item>)
              : null}
              {(!aadharCardVerified) ? (<Form.Item label=":">
                <div>
                  {
                    /**
                      * Disable aadhar card input 
                      * - if aadhaar verified
                      * - [otp generation is done, or processing is going on] i.e. aadhaarCardTransactionId is available
                      * - Upon button submissions [verify/final submit]
                    */
                  }
                  <Button
                      className={classes.verifyButton}
                      loading={isGeneratingOTP}
                      disabled={ !aadharCardGoogleCaptcha || aadhaarCardTransactionId || processingVerify || processingSubmit}
                      onClick={verifyAadharCardAndCaptcha}>
                      {intl.formatMessage({ id: "generateAadhaarOtp" })}
                  </Button>
                </div>
              </Form.Item>) : null}
              {
                /** Take OTP Input if aadharCardTxn is generated at ongrid end & card is not verified */
                aadhaarCardTransactionId && !aadharCardVerified ?
                (
                  <>
                    <Form.Item
                      label={`${intl.formatMessage({ id: 'otp' })} ::`}
                      name={"otp"}
                      rules={[
                          {
                              required: true,
                              message: `${intl.formatMessage({ id: "otp" })} is required!`,
                          },
                          {
                            pattern: new RegExp(/^[0-9]{6}$/),
                            message: `${intl.formatMessage({ id: "otp" })} should be 6 digits!`,
                          }
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({ id: 'otp' })}
                        maxLength={12}
                        onChange={(e) => {
                          const numberOnly = e.target.value.replace(/\D/g, "");
                          form.setFieldValue('otp', numberOnly);
                        }}
                        disabled={aadharCardVerified || processingVerify || processingSubmit}
                        type={visibleOtp ? 'text' : 'password'}
                        suffix={
                          <span onClick={toggleVisibility} style={{ cursor: 'pointer' }}>
                            {visibleOtp ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          </span>
                        }
                      />
                    </Form.Item>
                    <Form.Item label=":">
                      <div>
                          <Button
                              className={classes.verifyButton}
                              loading={processingVerify}
                              disabled={aadharCardVerified || processingVerify || processingSubmit}
                              onClick={validateOtp}>
                              {intl.formatMessage({ id: "verifyAadhaarOtp" })}
                          </Button>
                      </div>
                    </Form.Item>
                  </>
                ) : null
              }
              <Form.Item
                label={`${intl.formatMessage({ id: "email" })} ::`}
                name="email"
                rules={[
                  {
                    type: 'email',
                    message: `${intl.formatMessage({ id: "email" })} is required!`,
                  },
                ]}
              >
                <Input
                  type='email'
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "email" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "fatherName" })} ::`}
                name="father_name"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "fatherName" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "fatherName" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "drivingLicenceNumber" })} ::`}
                name="driving_licence_number"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "drivingLicenceNumber" })} is required!`,
                  },
                  {
                    pattern: new RegExp(/^([A-Z]{1,2})(\d{2}|\d{3})[a-zA-Z]{0,1}(\d{4})(\d{7})$/),
                    message: `Invalid ${intl.formatMessage({ id: "drivingLicenceNumber" })}`,
                  }
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "drivingLicenceNumber" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "panNumber" })} ::`}
                name="pan_number"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "panNumber" })} is required!`,
                  },
                  {
                    pattern: new RegExp(/^[A-Z0-9a-z]{10}/),
                    message: `Invalid ${intl.formatMessage({ id: "panNumber" })}`,
                  }
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "panNumber" })}
                />
              </Form.Item>
              <div className={classes.formSectionHeader}>
                {intl.formatMessage({ id: "bankDetails" })} :
              </div>
              <Form.Item
                label={`${intl.formatMessage({ id: "bankIFSCCode" })} ::`}
                name="bank_ifsc_code"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "bankIFSCCode" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "bankIFSCCode" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "bankAccountNumber" })} ::`}
                name="bank_account_number"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "bankAccountNumber" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "bankAccountNumber" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "bankProofDocument" })} ::`}
                name="bank_proof_document"
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "bankProofDocument" })} is required!`,
                  },
                ]}
              >
                <Upload
                  accept=".png, .jpg, .jpeg"
                  multiple={true}
                  maxCount={2}
                  showUploadList={true}
                  listType="picture-card"
                  className={classes.uploadField}
                  onChange={file => {
                    if (file?.fileList?.length) {
                        setIdentificationDocumentList({
                          ...identificationDocumentList,
                          bankProofDocument: file.fileList,
                        })
                      } else {
                        setIdentificationDocumentList({
                          ...identificationDocumentList,
                          bankProofDocument: [],
                        })
                      }
                    }}
                  >
                <UploadOutlined className={classes.uploadIcon} />
              </Upload>
              </Form.Item>
              <div className={classes.formSectionHeader}>
                {intl.formatMessage({ id: "permanentAddressDetails" })} :
              </div>
              <Form.Item
                label={`${intl.formatMessage({ id: "permanentAddressCareOf" })} ::`}
                name={["permanent_address","care_of"]}
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "permanentAddressCareOf" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "permanentAddressCareOf" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "permanentAddressLine1" })} ::`}
                name={["permanent_address","address"]}
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "permanentAddressLine1" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "permanentAddressLine1" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "permanentAddressLocality" })} ::`}
                name={["permanent_address","locality"]}
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "permanentAddressLocality" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "permanentAddressLocality" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "permanentAddressLandmark" })} ::`}
                name={["permanent_address","landmark"]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "permanentAddressLandmark" })}
                />
              </Form.Item>
              <Form.Item
                label={`${intl.formatMessage({ id: "permanentAddressCity" })} ::`}
                name={["permanent_address","city"]}
                rules={[
                  {
                    required: true,
                    message: `${intl.formatMessage({ id: "permanentAddressCity" })} is required!`,
                  },
                ]}
              >
                <Input
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  placeholder={intl.formatMessage({ id: "permanentAddressCity" })}
                />
              </Form.Item>
              <div className={classes.formSectionHeader}>
                {intl.formatMessage({ id: "presentAddressDetails" })} :
              </div>
              <Form.Item
                label={`${intl.formatMessage({ id: "presentAddressSameAsPermanent" })} ::`}
                name="is_present_permanent_address_same"
                valuePropName="checked"
              >
                <Radio.Group
                  disabled={!aadharCardVerified || processingVerify || processingSubmit}
                  onChange={(e) => setIsPresentPermanentAddressSame(e.target.value)}
                >
                  <Radio value={true}>{intl.formatMessage({ id: "yes" })}</Radio>
                  <Radio value={false}>{intl.formatMessage({ id: "no" })}</Radio>
                </Radio.Group>
              </Form.Item>
              {
                isPresentPermanentAddressSame ? null : (
                  <>
                    <Form.Item
                      label={`${intl.formatMessage({ id: "presentAddressLine1" })} ::`}
                      name={["present_address","address"]}
                      rules={[
                        {
                          required: true,
                          message: `${intl.formatMessage({ id: "presentAddressLine1" })} is required!`,
                        },
                      ]}
                    >
                      <Input
                        disabled={!aadharCardVerified || processingVerify || processingSubmit}
                        placeholder={intl.formatMessage({ id: "presentAddressLine1" })}
                      />
                    </Form.Item>
                    <Form.Item
                      label={`${intl.formatMessage({ id: "presentAddressLocality" })} ::`}
                      name={["present_address","locality"]}
                      rules={[
                        {
                          required: true,
                          message: `${intl.formatMessage({ id: "presentAddressLocality" })} is required!`,
                        },
                      ]}
                    >
                      <Input
                        disabled={!aadharCardVerified || processingVerify || processingSubmit}
                        placeholder={intl.formatMessage({ id: "presentAddressLocality" })}
                      />
                    </Form.Item>
                    <Form.Item
                      label={`${intl.formatMessage({ id: "presentAddressLandmark" })} ::`}
                      name={["present_address","landmark"]}
                    >
                      <Input
                        disabled={!aadharCardVerified || processingVerify || processingSubmit}
                        placeholder={intl.formatMessage({ id: "presentAddressLandmark" })}
                      />
                    </Form.Item>
                    <Form.Item
                      label={`${intl.formatMessage({ id: "presentAddressCity" })} ::`}
                      name={["present_address","city"]}
                      rules={[
                        {
                          required: true,
                          message: `${intl.formatMessage({ id: "presentAddressCity" })} is required!`,
                        },
                      ]}
                    >
                      <Input
                        disabled={!aadharCardVerified || processingVerify || processingSubmit}
                        placeholder={intl.formatMessage({ id: "presentAddressCity" })}
                      />
                    </Form.Item>
                  </>
                )
              }
              {aadharCardVerified &&
                (
                  <Form.Item label=":">
                    <div>
                        <ReCAPTCHA
                            sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                            onChange={captchaChange}
                            hl={language}
                            key={`${language}${formReset}`}
                        />
                        <span style={{ color: "red" }}>{captchaError}</span>
                    </div>
                  </Form.Item>
                )
              }
              <Form.Item label=":">
                  <Button
                      style={{ width: "100%" }}
                      size="large"
                      disabled={processingVerify || !aadharCardVerified || processingSubmit}
                      type="primary"
                      htmlType="submit"
                      loading={processingSubmit}
                  >
                      {intl.formatMessage({ id: "submit" })}
                  </Button>
              </Form.Item>
            </div>
        </Form>
    </>
    );
};

const mapStateToProps = (state) => {
    return {
        masterdata: state.masterdata,
    };
};

export default connect(mapStateToProps)(DealshareRiderOnboardingForm);